import React, { useRef, useState, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { ScopeCertificateDates } from '../../../sc-models/scope-certificate-details-dto';
import FormBuilderV4 from '../../../../../../shared/components/form-builder/form-builder-v4';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { Utills } from '../../../../../../shared/app-configs/utilit';
import { dtrackItServiceV4 } from '../../../sc-service/dtrackit.service';
import { RestrictedDataValidation } from '../../../../../../shared/utils/custom-validation.utils';
import { useApi } from '../../../../../../shared/hooks/useApi';
import useNotification from '../../../../../../shared/hooks/useNotification';
import { TCAppConfig } from '../../../../../../shared/app-configs/tc-app-configs';
import { useScStore } from '../../../../../../store/scStandard.store';


interface ScopeCertificateDatesFormProps {
  formRef: any;
  datesData: ScopeCertificateDates;
  setDatesData: React.Dispatch<React.SetStateAction<ScopeCertificateDates>>;
  scStatus?: string;
  isAmendment?: boolean;
  isSuspended?: boolean;
  facilityMinAuditDate?: string;
  facilityMaxAuditDate?: string;
  isLocked?: boolean;
  setIsFormDirty?: (val: boolean) => void;
}

const ScopeCertificateDatesForm: React.FC<ScopeCertificateDatesFormProps> = ({
  formRef,
  datesData,
  setDatesData,
  scStatus,
  isAmendment,
  isSuspended,
  facilityMinAuditDate,
  facilityMaxAuditDate,
  isLocked,
  setIsFormDirty,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const { notify } = useNotification();
  const { executeGet } = useApi();
  const [accreditationBodyOptions, setAccreditationBodyOptions] = useState<Array<{value: string, label: string}>>([]);
  const [statusOptions, setStatusOptions] = useState<Array<{value: string, label: string}>>([]);
  const [suspensionReasonOptions, setSuspensionReasonOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState(true);

  // M3 + M4: Status transition rules
  const originalStatus = datesData.scStatus || '';
  const isStatusLocked = ['E', 'W'].includes(originalStatus);
  const STATUS_TRANSITIONS: Record<string, string[]> = {
    'I': ['V', 'S', 'W'],          // Issued → Valid, Suspended, Withdrawn
    'V': ['E', 'S', 'W'],          // Valid → Expired, Suspended, Withdrawn
    'S': ['E', 'V', 'W'],          // Suspended → Expired, Valid, Withdrawn
    'E': [],                        // Expired → no transitions (locked)
    'W': [],                        // Withdrawn → no transitions (locked)
    '': [],                         // New SC — all options; transition filter skipped
  };
  const allowedTransitionCodes = STATUS_TRANSITIONS[originalStatus];
  
  // Check if scValidFromDate is in the future
  const isFutureDate = datesData.scValidFromDate && new Date(String(datesData.scValidFromDate)) > new Date();
  const allowedFutureStatuses = ["I", "V"];
  
  const filteredStatusOptions = statusOptions.filter((option) => {
   if (isFutureDate && !allowedFutureStatuses.includes(String(option.value))) return false;
    if (originalStatus && allowedTransitionCodes) {
      if (option.value === originalStatus) return true;
      return allowedTransitionCodes.includes(String(option.value));
    }
    return true;
  });
  
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setDatesData(datesData);
  };



  // Get states from API with search and pagination
  const getStates = async (searchQuery: string, offset: number = 0) => {
    try {
      
      const queryParams: any = {
        query_param: 'ST',
        limit: 10,
        offset: offset
      };
      
      if (searchQuery) {
        queryParams.state_desc = searchQuery;
      }
      
      const result: any = await executeGet(
        TCAppConfig.GET_DTRACKIT_REF_CODE_V4,
        queryParams
      );
      
      
      if (result?.data && Array.isArray(result.data)) {
        // Return the raw data for SearchInput component
        return result.data;
      } else {
        return [];
      }
    } catch (err) {
      notify('Error in Fetching States', 'error');
      Utills.cl(`Error in Fetching States ${err}`);
      return [];
    }
  };

  // Get accreditation body options from API
  const getAccreditationBody = async () => {
    try {
      const result: any = await dtrackItServiceV4.getAccreditationBody();
      
      if (result?.data && Array.isArray(result.data)) {
        const accreditationBodies = result.data.map((item: any) => ({
          value: item.code_1,
          label: item.description_1,
        }));
        setAccreditationBodyOptions(accreditationBodies);
      } else {
        setAccreditationBodyOptions([]);
      }
    } catch (err) {
      Utills.cl(`Error in Fetching Accreditation Body ${err}`);
      setAccreditationBodyOptions([]);
    }
  };

  const getDropdownOptions = async () => {
    try {
      setLoading(true);
      // Fetch all dropdown options in parallel — loading stays true until ALL resolve
      // so FormBuilderV4 never renders with empty option arrays (prevents MUI out-of-range warnings)
      const [scStatusResponse, suspensionReasonResponse] = await Promise.all([
        dtrackItServiceV4.getSCStatusV4(),
        dtrackItServiceV4.getSuspensionReason(),
        getAccreditationBody(), // included here so its result is ready before loading=false
      ]);

      if (scStatusResponse?.data) {
        setStatusOptions(scStatusResponse.data.map((item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
        })));
      }

      if (suspensionReasonResponse?.data) {
        setSuspensionReasonOptions(suspensionReasonResponse.data.map((item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
        })));
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDropdownOptions();
  }, []);

  // ─── Normalize scPlaceOfIssue at the data level (not in useEffect) ──────────────
  // FormBuilderV4 uses enableReinitialize, so whenever datesData changes Formik resets
  // to initialValues. Parsing in a useEffect → setValues loses the race against that
  // reset and the SearchInput stays blank. By computing the display values here and
  // passing them as initialValues, enableReinitialize always wins WITH correct data.
  const normalizedDatesData = useMemo(() => {
    const base: any = { ...datesData };
    const placeOfIssue = datesData?.scPlaceOfIssue;
    if (!placeOfIssue) return base;

    let stateCode: string | undefined;
    let stateName: string | undefined;

    // ISO 3166-2 codes can be:
    //   - Letter-only:    "IN-TN", "AU-VIC"
    //   - Alphanumeric:   "MN 051", "CN-11", "US-CA"
    //   - Already full:   stored directly from API code_1
    // Treat any value that does NOT contain a comma and does NOT look like a raw
    // legacy string ("India(IN),Tamil Nadu(TN)") as an already-resolved code.
    const isAlreadyCode = !placeOfIssue.includes(',') && !placeOfIssue.match(/^[A-Za-z\s]+\([^)]+\)$/);

    if (isAlreadyCode) {
      // Code is already stored cleanly ("IN-TN", "MN 051", "AU-VIC", etc.) — use as-is
      stateCode = placeOfIssue;
      if (!base.scStateOrProvince) base.scStateOrProvince = stateCode;
    } else {
      // Multi-format parser for legacy formats:
      //   A) "India(IN),Tamil Nadu(TN)"         — API, comma no space, short code
      //   B) "Country (CC), StateName (CC-SC)"  — old legacy, space after comma
      //   C) "StateName (CC-SC)"                — no country prefix
      const parts = placeOfIssue.split(',');
      if (parts.length >= 2) {
        const countryPart  = parts[0].trim();
        const rawStatePart = parts.slice(1).join(',').trim();
        const ccMatch = countryPart.match(/\(([^)]+)\)$/);
        const countryCode = ccMatch ? ccMatch[1].trim() : '';
        const sm = rawStatePart.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
        if (sm) {
          stateName = sm[1].trim();
          const rawCode = sm[2].trim();
          // If rawCode already contains country prefix (has '-' or ' '), use as-is.
          // Only prepend countryCode when it looks like a real ISO 2-3 letter code (e.g. "IN", "AU").
          // This prevents descriptive words in parens — like "South" in "Korea (South)" — from
          // being incorrectly used as a country code prefix, e.g. producing "South-KR" instead of "KR".
          const isValidISOCountryCode = /^[A-Z]{2,3}$/.test(countryCode);
          stateCode = (rawCode.includes('-') || rawCode.includes(' '))
            ? rawCode
            : (isValidISOCountryCode && countryCode) ? `${countryCode}-${rawCode}` : rawCode;
        } else {
          stateName = rawStatePart;
        }
      } else {
        const sm = placeOfIssue.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
        if (sm) { stateName = sm[1].trim(); stateCode = sm[2].trim(); }
        else    { stateName = placeOfIssue; }
      }

      if (stateName) {
        base.scStateOrProvince        = stateCode ? `${stateName} (${stateCode})` : stateName;
        base.scStateOrProvinceDescription = stateName;
      }
      if (stateCode) {
        base.scPlaceOfIssue          = stateCode; // store whatever code we resolved
        base.scStateOrProvinceObject = { code_1: stateCode, description_1: stateName || stateCode };
      }
    }
    return base;
  }, [datesData]);

  // Sync date auto-update fields whenever datesData changes
  useEffect(() => {
    if (!formRef?.current) return;
    // Always keep Formik in sync with the normalized snapshot
    formRef.current.setValues(normalizedDatesData);
    // Auto-update scValidFromDate when createdDate is later than current validFromDate
    if (datesData.scCreatedDate) {
      const currentValidFromDate = datesData.scValidFromDate;
      if (!currentValidFromDate || new Date(datesData.scCreatedDate) > new Date(currentValidFromDate)) {
        formRef.current.setFieldValue('scValidFromDate', datesData.scCreatedDate);
      }
    }
    // Auto-fill scLastUpdatedDate on first creation
    if (datesData.scCreatedDate && !isAmendment && !datesData.scLastUpdatedDate) {
      formRef.current.setFieldValue('scLastUpdatedDate', datesData.scCreatedDate);
    }
  }, [datesData, formRef]);



  // Commented out hardcoded options - now using API calls
  // const accreditationBodyOptions = [
  //   { label: "SLAB", value: "SLAB" },
  //   { label: "IOAS", value: "IOAS" },
  //   { label: "ANAB", value: "ANAB" },
  //   { label: "TAF", value: "TAF" },
  //   { label: "COFRAC", value: "COFRAC" },
  //   { label: "IA Japan", value: "IAJapan" },
  //   { label: "DAkkS", value: "DAkkS" },
  // ];

  // Silently normalize any NA variant → "na" before Yup tests run (good UX: no error shown).
  // Regex covers every case/separator combination:
  //   NA, Na, nA, na, N/A, N/a, n/A, N.A, N-A, "N A", na., N.A., etc.
  // Plus common English phrases: "not available", "null", "nil", "none".
  const isNaLike = (v?: string | null): boolean => {
    if (!v) return false;
    const s = v.trim().toLowerCase();
    // Match: n + optional whitespace/slash/dot/dash + a + optional trailing dot
    if (/^n\s*[\/\-\.]?\s*a\.?$/.test(s)) return true;
    return ['not available', 'null', 'nil', 'none'].includes(s);
  };
  const normalizeNaVariant = (value: string | undefined | null) => {
    if (!value) return value;
    return isNaLike(value) ? 'na' : value;
  };

  const autoComputeSCStatus = (formValues: any, setFieldValue: any) => {
    if (originalStatus !== '') return; // Only for new SCs
    
    const today = moment().startOf('day');
    const createdDate = formValues.scCreatedDate ? moment(formValues.scCreatedDate).startOf('day') : null;
    const validFrom = formValues.scValidFromDate ? moment(formValues.scValidFromDate).startOf('day') : null;
    const validUntil = formValues.scValidUntilDate ? moment(formValues.scValidUntilDate).startOf('day') : null;
    const extendedUntil = formValues.scExtendedUntilDate ? moment(formValues.scExtendedUntilDate).startOf('day') : null;
    
    let expiryDate = validUntil ? validUntil.clone() : null;
    if (expiryDate && extendedUntil && extendedUntil.isAfter(expiryDate)) {
      expiryDate = extendedUntil.clone();
    }

    if (expiryDate && today.isAfter(expiryDate)) {
      setFieldValue('scStatus', 'E');
      setFieldValue('scStatusDescription', 'Expired');
    } else if (validFrom && expiryDate && today.isSameOrAfter(validFrom) && today.isSameOrBefore(expiryDate)) {
      setFieldValue('scStatus', 'V');
      setFieldValue('scStatusDescription', 'Valid');
    } else if (createdDate && validFrom && today.isSameOrAfter(createdDate) && today.isBefore(validFrom)) {
      setFieldValue('scStatus', 'I');
      setFieldValue('scStatusDescription', 'Issued');
    }
  };

  const formConfig = {
    pageName: "scope-certificate-details",
    // initialValues uses normalizedDatesData (not raw datesData) so that:
    //   1. scStateOrProvince is already the display string "Tamil Nadu (IN-TN)"
    //   2. scPlaceOfIssue is the canonical code "IN-TN" (passes Yup regex)
    //   3. enableReinitialize always resets with correct values — no race condition
    initialValues: (!isAmendment && normalizedDatesData.scCreatedDate && !normalizedDatesData.scLastUpdatedDate)
      ? { ...normalizedDatesData, scLastUpdatedDate: normalizedDatesData.scCreatedDate }
      : normalizedDatesData,
    onValuesChange: (values: any) => {
      const hasChanges = JSON.stringify(values) !== JSON.stringify(datesData);
      if (setIsFormDirty) setIsFormDirty(hasChanges);
    },
    validationSchema: Yup.object().shape({
      scStatus: Yup.string().required("SC Status is required"),
      scStatusDescription: Yup.string(),
      scSuspensionReason: Yup.string().when("scStatus", {
        is: "S",
        then: (schema) => schema.required("Suspension Reason is required"),
        otherwise: (schema) => schema.optional(),
      }),
      scSuspensionReasonDescription: Yup.string(),
      scCreatedDate: Yup.date()
        .required('SC First Created Date is required'),
      
      scLastUpdatedDate: Yup.date()
        .required('SC Last Updated Date is required')
        .max(new Date(), 'SC Last Updated Date cannot be greater than current date')
        .test('last-updated-sequence', 'Last Updated Date must be after or equal to First Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            return new Date(firstCreated) <= new Date(value);
          })
        .test('last-updated-amendment', 'During amendments, Last Updated Date must be after or equal to Created Date',
          function(value) {
            if (!isAmendment || !value) return true;
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated) return true;
            return new Date(firstCreated) <= new Date(value);
          }),
      
      scExtendedUntilDate: Yup.date()
        .nullable()
        // B2: must be STRICTLY after Valid Until Date (equal not allowed)
        .test('extended-until-sequence', 'Extended Until Date must be strictly after Valid Until Date',
          function(value) {
            const validUntil = this.parent.scValidUntilDate; // Fixed: was scValidFromDate
            if (!validUntil || !value) return true;
            return new Date(value) > new Date(validUntil); // Strict >
          })
        .test('expired-status-extended-date-restriction', 'Extended Until Date cannot be in the future when SC Status is Expired',
          function(value) {
            const status = this.parent.scStatus;
            if (status !== 'E' || !value) return true; // only validate if status is Expired
            const today = new Date();
            today.setHours(0, 0, 0, 0); // normalize to start of day for comparison
            const extendedUntilDate = new Date(value);
            extendedUntilDate.setHours(0, 0, 0, 0); // normalize to start of day
            return extendedUntilDate <= today; // Extended Until Date must be on or before today
          }),
      
      scLastAuditStartDate: Yup.date()
        .required('SC Last Audit Start Date is required')
        .test('audit-start-before-created', 'SC Last Audit Start Date must be less than or equal to SC Created Date',
          function(value) {
            const createdDate = this.parent.scCreatedDate;
            if (!createdDate || !value) return true;
            return new Date(value) <= new Date(createdDate);
          })
        .test('audit-start-sequence', 'Last Audit Start Date must be before or equal to Last Audit Date',
          function(value) {
            const auditDate = this.parent.scLastAuditDate;
            if (!auditDate || !value) return true;
            return new Date(value) <= new Date(auditDate);
          }),
      
      scValidFromDate: Yup.date()
        .required('SC Valid From Date is required')
        .test('valid-from-sequence', 'Valid From Date must be after or equal to First Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            return new Date(firstCreated) <= new Date(value);
          })
        .test('valid-from-max-days', 'SC Valid From Date cannot be more than 60 days after SC Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            const createdDate = new Date(firstCreated);
            const validFromDate = new Date(value);
            const daysDifference = Math.ceil((validFromDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
            return daysDifference <= 60;
          }),
      
      scValidUntilDate: Yup.date()
        .required('SC Valid Until Date is required')
        .test('valid-until-sequence', 'Valid Until Date must be after SC Valid From Date',
          function(value) {
            const validFrom = this.parent.scValidFromDate;
            if (!validFrom || !value) return true;
            const validFromDate = new Date(validFrom);
            const validUntilDate = new Date(value);
            const daysDifference = Utills.calculateDateDifferenceInDays(validFromDate, validUntilDate);
            return daysDifference > 0;
          })
        .test('valid-until-max-period', 'Validity period exceeds the maximum limit for standard(s)',
          function(value) {
            const validFrom = this.parent.scValidFromDate;
            if (!validFrom || !value) return true;

            const { scStandards } = useScStore.getState();
            if (!scStandards || scStandards.length === 0) return true;

            const daysDifference = moment(value).diff(moment(validFrom), 'days');

            const oneYearStandards = ['GRS', 'RCS', 'OCS', 'RDS'];
            const threeYearStandards = ['RWS', 'RAS', 'RMS'];

            const hasOneYear = scStandards.some((std: string) => oneYearStandards.includes(std));
            const hasThreeYears = scStandards.some((std: string) => threeYearStandards.includes(std));

            if (hasOneYear) {
              if (daysDifference > 365) {
                const oneYearStds = scStandards.filter((std: string) => oneYearStandards.includes(std));
                return this.createError({
                  message: `Maximum certificate validity period is 1 year (365 days) for ${oneYearStds.join(', ')} standard${oneYearStds.length > 1 ? 's' : ''}.`
                });
              }
            } else if (hasThreeYears) {
              if (daysDifference > 1095) {
                const threeYearStds = scStandards.filter((std: string) => threeYearStandards.includes(std));
                return this.createError({
                  message: `Maximum certificate validity period is 3 years (1095 days) for ${threeYearStds.join(', ')} standard${threeYearStds.length > 1 ? 's' : ''}.`
                });
              }
            }
            return true;
          })
        .test('expired-status-date-restriction', 'Valid Until Date cannot be in the future when SC Status is Expired',
          function(value) {
            const status = this.parent.scStatus;
            if (status !== 'E' || !value) return true; // only validate if status is Expired
            const today = new Date();
            today.setHours(0, 0, 0, 0); // normalize to start of day for comparison
            const validUntilDate = new Date(value);
            validUntilDate.setHours(0, 0, 0, 0); // normalize to start of day
            return validUntilDate <= today; // Valid Until Date must be on or before today
          }),
      
      scWithdrawalDate: Yup.date()
        .when('scStatus', {
          is: 'W',
          then: (schema) => schema.required('Withdrawal Date is required when SC Status is Withdrawn'),
          otherwise: (schema) => schema.nullable()
        })
        .test('withdrawal-sequence', 'Withdrawal Date must be after or equal to Valid From Date',
          function(value) {
            const validFrom = this.parent.scValidFromDate;
            if (!validFrom || !value) return true;
            return new Date(validFrom) <= new Date(value);
          }),
      
      scLastAuditDate: Yup.date()
        .required('SC Last Audit Date is required')
        .test('audit-date-before-created', 'SC Last Audit Date must be less than or equal to SC Created Date',
          function(value) {
            const createdDate = this.parent.scCreatedDate;
            if (!createdDate || !value) return true;
            return new Date(value) <= new Date(createdDate);
          })
        .test('audit-date-sequence', 'Last Audit Date must be after or equal to Last Audit Start Date', 
          function(value) {
            const startDate = this.parent.scLastAuditStartDate;
            if (!startDate || !value) return true;
            return new Date(startDate) <= new Date(value);
          }),
      
      scStateOrProvince: Yup.mixed()
        .test('state-required', 'Select State or Province is required', (value) => {
          // Accept any truthy value - SearchInput briefly stores the full object before
          // events.onChange overwrites it with a display string; both are valid selections
          if (value === null || value === undefined || value === '') return false;
          if (typeof value === 'string') return value.trim().length > 0;
          return true; // object = a valid selection was made
        }),
      
      scCbAccreditationBody: Yup.string()
        .required('SC CB Accreditation Body is required'),
      
      scCbAccreditationBodyDescription: Yup.string(),
      
      scCbScopeStipulations: Yup.string()
        // Auto-normalize NA variants → "na" so the user never sees a validation error for "n/a" etc.
        .transform(normalizeNaVariant)
        .required('SC CB Scope Stipulations is required')
        // M8: whitespace-only not allowed
        .test('no-whitespace', 'Whitespace-only value is not allowed in Scope Stipulations', (value) => {
          if (!value) return true;
          return value.trim().length > 0;
        })
        // M8: <=6 chars rejected unless exactly "na" (transform guarantees only "na" reaches here)
        .test('min-length-or-na', 'Values with 6 or fewer characters are not allowed (except "na")', (value) => {
          if (!value) return true;
          if (value.trim() === 'na') return true;
          return value.trim().length > 6;
        }),
      
      scCbAccreditationNo: Yup.string()
        .nullable()
        .max(50, 'Max 50 characters')
        // Accepted: empty string or valid string(50). Rejected: whitespace-only string.
        .test('no-whitespace-only', 'Whitespace-only value is not allowed for SC CB Accreditation Number', (value) => {
          if (!value || value === '') return true; // empty/blank → allowed
          return value.trim().length > 0;          // whitespace-only → rejected
        }),

      scPlaceOfIssue: Yup.string()
        .required('SC Place of Issue is required')
        // Accept any ISO 3166-2 style code: letter-only (IN-TN, AU-VIC) OR
        // alphanumeric with space/hyphen (MN 051, CN-11) OR bare country/state
        // letter code (NL, SG, etc.). Max length 10 to cover all known ISO subdivision codes.
        .max(10, 'SC Place of Issue code is too long')
        .matches(
          /^[A-Z0-9]{1,3}[-\s][A-Z0-9]{1,6}$|^[A-Z]{2}-[A-Z]{2,3}$|^[A-Z]{2,3}$/,
          'Invalid state/province code format'
        ),
    }),
    // Rule: Expired → entire form is read-only (no changes allowed at all).
    // Rule: Withdrawn → form is NOT fully read-only here so individual fields can use values.scStatus to toggle.
    // Rule: Suspended → form is read-only EXCEPT the Status dropdown (user can change it out of S).
    // hasSubmitSC (view-only route) also locks the form.
    // Also lock if tab is COMPLETED (isLocked).
    readOnly: hasSubmitSC || (isStatusLocked && originalStatus !== 'W') || Boolean(isSuspended) || isLocked,
    sections: [
      // ── 1. Certificate Dates — ALL dates in one place ─────────────────────
      {
        title: 'Certificate Dates',
        fields: [
          {
            label: 'SC Created Date',
            name: 'scCreatedDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => Boolean(isAmendment) || values.scStatus === 'W',
            events: {
              onChange: (newValue: any, updatedValues: any, setFieldValue: any) => {
                if (newValue && formRef?.current) {
                  const currentValidFromDate = updatedValues?.scValidFromDate;
                  if (!currentValidFromDate || new Date(newValue) > new Date(currentValidFromDate)) {
                    formRef.current.setFieldValue('scValidFromDate', newValue);
                  }
                  const currentLastUpdated = updatedValues?.scLastUpdatedDate;
                  if (!isAmendment && !currentLastUpdated) {
                    formRef.current.setFieldValue('scLastUpdatedDate', newValue);
                  } else if (currentLastUpdated && new Date(newValue) > new Date(currentLastUpdated)) {
                    formRef.current.setFieldValue('scLastUpdatedDate', newValue);
                  }
                  autoComputeSCStatus({ ...updatedValues, scCreatedDate: newValue }, setFieldValue);
                }
              },
            },
            props: (values: any) => ({
              placeholder: 'YYYY-MM-DD',
              maxDate: values?.scValidFromDate ? moment(values.scValidFromDate) : moment().add(100, 'years'),
              minDate: values?.scValidFromDate ? moment(values.scValidFromDate).subtract(60, 'days') : undefined,
            }),
          },
          {
            label: 'SC Last Updated Date',
            name: 'scLastUpdatedDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => values.scStatus === 'W',
            props: (values: any) => ({
              placeholder: 'YYYY-MM-DD',
              minDate: values?.scCreatedDate ? moment(values.scCreatedDate) : undefined,
              disableFuture: true,
              maxDate: moment(),
            }),
          },
          {
            label: 'SC Valid From Date',
            name: 'scValidFromDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => Boolean(isAmendment) || values.scStatus === 'W',
            events: {
              onChange: (newValue: any, updatedValues: any, setFieldValue: any) => {
                autoComputeSCStatus({ ...updatedValues, scValidFromDate: newValue }, setFieldValue);
              }
            },
            props: (values: any) => ({
              placeholder: 'YYYY-MM-DD',
              maxDate: values?.scCreatedDate ? moment(values.scCreatedDate).clone().add(60, 'days') : moment().add(100, 'years'),
              minDate: values?.scCreatedDate ? moment(values.scCreatedDate) : undefined,
            }),
          },
          {
            label: 'SC Valid Until Date',
            name: 'scValidUntilDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => Boolean(isAmendment) || values.scStatus === 'W',
            events: {
              onChange: (newValue: any, updatedValues: any, setFieldValue: any) => {
                autoComputeSCStatus({ ...updatedValues, scValidUntilDate: newValue }, setFieldValue);
              }
            },
            props: (values: any) => ({
              placeholder: 'YYYY-MM-DD',
              maxDate: values?.scStatus === 'E' ? moment().endOf('day') : moment().add(100, 'years'),
              minDate: values?.scValidFromDate ? moment(values.scValidFromDate).add(1, 'day') : undefined,
            }),
          },
          {
            label: 'SC Extended Until Date',
            name: 'scExtendedUntilDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            disabled: (values: any) => values.scStatus === 'W',
            events: {
              onChange: (newValue: any, updatedValues: any, setFieldValue: any) => {
                autoComputeSCStatus({ ...updatedValues, scExtendedUntilDate: newValue }, setFieldValue);
              }
            },
            props: (values: any) => ({
              placeholder: 'YYYY-MM-DD',
              maxDate: values?.scStatus === 'E' ? moment().endOf('day') : moment().add(100, 'years'),
              minDate: values?.scValidUntilDate ? moment(values.scValidUntilDate).add(1, 'day') : undefined,
            }),
          },
          {
            label: 'SC Last Audit Start Date',
            name: 'scLastAuditStartDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => values.scStatus === 'W',
             props: (values: any) => ({
    placeholder: 'YYYY-MM-DD',
    maxDate: values?.scCreatedDate
      ? moment(values.scCreatedDate)
      : moment(),
  }),
          },
          {
            label: 'SC Last Audit Date',
            name: 'scLastAuditDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => values.scStatus === 'W',
            props: (values: any) => ({
    placeholder: 'YYYY-MM-DD',
    maxDate: values?.scCreatedDate
      ? moment(values.scCreatedDate)
      : moment(),
    minDate: values?.scLastAuditStartDate
      ? moment(values.scLastAuditStartDate)
      : undefined,
  }),
          },
          {
            label: 'SC Withdrawal Date',
            name: 'scWithdrawalDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: (values: any) => values?.scStatus === 'W',
            disabled: (values: any) => values?.scStatus !== 'W',
            readOnly: (values: any) => values?.scStatus !== 'W',
            props: (values: any) => ({
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, 'years'),
              minDate: values?.scValidFromDate ? moment(values.scValidFromDate) : undefined,
            }),
          },
        ],
      },

      // ── 2. SC Status ───────────────────────────────────────────────────────
      {
        title: 'SC Status',
        fields: [
          {
            label: 'SC Status',
            name: 'scStatus',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            readOnly: originalStatus === 'E',
            disabled: (values: any) => originalStatus === 'E',
            props: {
              options: filteredStatusOptions,
              placeholder: 'Select SC Status',
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (field: string, value: any) => void
              ) => {
                if (value !== 'S') {
                  setFieldValue('scSuspensionReason', '');
                  setFieldValue('scSuspensionReasonDescription', '');
                }
                const selectedOption = filteredStatusOptions.find((option) => option.value === value);
                if (selectedOption) {
                  setFieldValue('scStatusDescription', selectedOption.label);
                }
              },
            },
          },
          {
            label: 'SC Suspension Reason',
            name: 'scSuspensionReason',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: (values: any) => values.scStatus === 'S',
            disabled: (values: any) => values.scStatus !== 'S',
            visible: (values: any) => values.scStatus === 'S',
            props: {
              options: suspensionReasonOptions,
              placeholder: 'Select Suspension Reason (if applicable)',
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (field: string, value: any) => void
              ) => {
                const selectedOption = suspensionReasonOptions.find((option) => option.value === value);
                if (selectedOption) {
                  setFieldValue('scSuspensionReasonDescription', selectedOption.label);
                }
              },
            },
          },
        ],
      },

      // ── 3. Place of Issue & Accreditation Details ──────────────────────────
      {
        title: 'Place of Issue & Accreditation Details',
        fields: [
          {
            label: 'Select State or Province',
            name: 'scStateOrProvince',
            type: 'search',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => values.scStatus === 'W',
            events: {
              onChange: (selectedState: any) => {
                if (selectedState) {
                  formRef.current?.setFieldValue('scStateOrProvinceObject', selectedState, false);
                  formRef.current?.setFieldValue('scStateOrProvinceDescription', selectedState.description_1, false);
                  formRef.current?.setFieldValue('scPlaceOfIssue', selectedState.code_1 || '', false);
                  formRef.current?.setFieldValue('scStateOrProvince', `${selectedState.description_1} (${selectedState.code_1})`, true);
                } else {
                  formRef.current?.setFieldValue('scStateOrProvinceObject', null, false);
                  formRef.current?.setFieldValue('scStateOrProvinceDescription', '', false);
                  formRef.current?.setFieldValue('scPlaceOfIssue', '', false);
                  formRef.current?.setFieldValue('scStateOrProvince', '', true);
                }
              },
            },
            props: {
              displayKey: 'description_1 code_1',
              placeholder: 'Search State or Province',
              param: 'ST',
              showCodeInDropdown: true,
              onSearch: async (searchQuery: string, offset: number) => {
                return await getStates(searchQuery, offset);
              },
            },
          },
          {
            label: 'SC CB Accreditation Body',
            name: 'scCbAccreditationBody',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: (values: any) => values.scStatus === 'W',
            events: {
              onChange: (newValue: any, _updatedValues: any, setFieldValue: any) => {
                const selectedAccreditationBody = accreditationBodyOptions.find(body => body.value === newValue);
                if (selectedAccreditationBody) {
                  setFieldValue('scCbAccreditationBodyDescription', selectedAccreditationBody.label, false);
                }
              },
            },
            props: {
              options: accreditationBodyOptions,
              placeholder: 'Select SC CB Accreditation Body',
            },
          },
          {
            label: 'SC CB Accreditation Number',
            name: 'scCbAccreditationNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            disabled: (values: any) => values.scStatus === 'W',
            props: {
              placeholder: 'Enter SC CB Accreditation Number',
            },
          },
        ],
      },

      // ── 4. SC CB Scope Stipulations ────────────────────────────────────────
      {
        title: 'SC CB Scope Stipulations',
        fields: [
          {
            label: 'SC CB Scope Stipulations',
            name: 'scCbScopeStipulations',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: (values: any) => values.scStatus === 'W',
            helperText: 'Please fill "na" if SC CB Scope Stipulations is not applicable',
            props: {
              placeholder: 'Enter SC CB Scope Stipulations',
            },
            events: {
              onChange: (value: any, _formValues: any, setFieldValue: (field: string, value: any) => void) => {
                const firstWord = typeof value === 'string' ? value.trim().split(/\s+/)[0] : '';
                const firstWordIsNot = firstWord.toLowerCase() === 'not';
                if (isNaLike(value) || firstWordIsNot) {
                  setFieldValue('scCbScopeStipulations', 'na');
                }
              },
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      const selectedState = values.scStateOrProvinceObject;

      // Build canonical scPlaceOfIssue ("IN-TN" format, 4-6 chars) for backend.
      // Priority: freshly selected state object → already-canonical Formik value → parse legacy raw format
      let placeOfIssue: string = selectedState?.code_1 || '';

      if (!placeOfIssue) {
        const raw = values.scPlaceOfIssue || '';
        // Accept any non-empty stored code as-is — the API's code_1 is authoritative
        // (covers letter-only "IN-TN" as well as alphanumeric "MN 051", "CN-11", etc.)
        if (raw && !raw.includes('(') && !raw.includes(',')) {
          // Already a clean code
          placeOfIssue = raw;
        } else if (raw.includes(',')) {
          // Legacy API format: "India(IN),Tamil Nadu(TN)" → extract state code
          const parts = raw.split(',');
          const ccMatch = parts[0].trim().match(/\(([^)]+)\)$/);
          const countryCode = ccMatch ? ccMatch[1].trim() : '';
          const sm = parts.slice(1).join(',').trim().match(/^(.+?)\s*\(([^)]+)\)\s*$/);
          if (sm) {
            const rawCode = sm[2].trim();
            placeOfIssue = (rawCode.includes('-') || rawCode.includes(' '))
                         ? rawCode
                         : countryCode ? `${countryCode}-${rawCode}` : rawCode;
          }
        }
      }

      const updatedValues = {
        ...values,
        scPlaceOfIssue: placeOfIssue,
        scPlaceOfIssueDescription: values?.scStateOrProvinceDescription ?? selectedState?.description_1 ?? "",
        // Normalize ALL NA variants → 'na' before saving
        scCbScopeStipulations: (() => {
          const raw = (values.scCbScopeStipulations || '').trim();
          return isNaLike(raw) ? 'na' : raw;
        })(),
        // Trim accreditation number: empty/null → keep as-is; whitespace-only → convert to empty string
        scCbAccreditationNo: typeof values.scCbAccreditationNo === 'string'
          ? values.scCbAccreditationNo.trim()
          : (values.scCbAccreditationNo ?? ''),
      };

      setDatesData(updatedValues);
      toggleDrawer();
    },
  };

  // Block render until all dropdown options are loaded.
  // Without this guard, MUI Select warns "out-of-range value" because the form renders
  // with an existing value (e.g. 'I', 'SLAB') before options arrive from the API.
  if (loading) return null;

  return <FormBuilderV4 
    formConfig={formConfig} 
    onSubmit={formConfig.handleSubmit} 
    formRef={formRef} 
  />;
};

export default ScopeCertificateDatesForm;

